import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Fade, CircularProgress } from "@mui/material";
import { motion } from "framer-motion";

import portals from "./portals";
import PortalCard from "./PortalCard";
import CloseIcon from "./CloseIcon";
import bg_ok from "../resources/bg_ok.png";
import { getTimeElapsed } from "../utils/getTimeElapsed";
import apiClient from "../utils/apiClient.js";

const cardVariants = {
  hidden: { opacity: 0, y: 20, borderColor: "rgba(255, 255, 255, 0)" },
  visible: (custom) => ({
    opacity: 1,
    y: 0,
    borderColor: "rgba(255, 255, 255, 0.3)",
    transition: {
      duration: 0.2,
      delay: custom.columnIndex * 0.1,
      ease: "easeOut",
    },
  }),
};

// framer-motion (12.11) laat een element bij een hardware-versnelde (WAAPI) opacity-fade
// 1 frame op de beginwaarde staan zodra de fade klaar is (flikkering). Met een onUpdate
// prop valt framer terug op de JS-animatie (zie ook noWaapi in App.js).
const noWaapi = () => {};

const closeButtonVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.3, delay: 1.8, ease: "easeOut" },
  },
};

const bgStyle = { position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 1 };

// Het plaatje dat het dashboard (linkerpaneel) al geladen heeft, mits het een
// variant van dezelfde achtergrond is. Dat staat dus direct in beeld.
const loadedDashboardSrc = (srcsetW) => {
  const el = document.querySelector(".left-panel-image");
  const cur = el && el.complete && el.naturalWidth > 0 ? el.currentSrc : "";
  if (!cur) return null;
  const urls = srcsetW.split(",").map((s) => s.trim().split(/\s+/)[0]);
  return urls.some((u) => cur === u || cur.endsWith(u)) ? cur : null;
};

// Manage Portals-achtergrond: precies het plaatje dat het dashboard al
// toont (staat dus meteen in beeld, geen aparte hogere resolutie laden).
// Niet beschikbaar (bv. nog niet geladen)? Dan zoals voorheen via srcset.
function DashboardBackground({ images }) {
  const [dashSrc] = useState(() => loadedDashboardSrc(images.srcsetW));
  if (dashSrc) return <img src={dashSrc} alt="Background" style={bgStyle} />;
  return <img src={images.src} srcSet={images.srcsetW} sizes={images.sizes} alt="Background" style={bgStyle} />;
}

function ManagePortals({
  backgroundImages = { src: bg_ok, srcset: "" },
  isSleeping = false,
  onBack,
}) {
  const [configuredPortals, setConfiguredPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showContent, setShowContent] = useState(false);
  const [computedOfflinePortals, setComputedOfflinePortals] = useState([]);
  const [computedOnlinePortals, setComputedOnlinePortals] = useState([]);
  const [computedNotAddedPortals, setComputedNotAddedPortals] = useState([]);
  const [computedNotAvailablePortals, setComputedNotAvailablePortals] = useState([]);
  const [animationKey, setAnimationKey] = useState(Date.now());
  const [selectedPortalForConfig, setSelectedPortalForConfig] = useState(null);
  const containerRef = useRef(null);
  const cardWidth = 364;
  const [maxContentWidth, setMaxContentWidth] = useState(cardWidth);
  const [marginLeft, setMarginLeft] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const resizeListenerRef = useRef(null);
  const [pollingBlocked, setPollingBlocked] = useState(false);

  const fetchConfiguredPortals = async () => {
    try {
      const organizationId = localStorage.getItem("organization_id");
      const token = localStorage.getItem("token");

      if (!organizationId || !token) {
        throw new Error("Missing organization_id or token in localStorage");
      }

      const response = await apiClient.get("/configured-portals", {
        params: { organization_id: organizationId },
        headers: { Authorization: `Bearer ${token}` },
      });

      let configuredPortalsRaw = Array.isArray(response.data) ? response.data : [];
      if (!configuredPortalsRaw.length) {
        throw new Error("No valid portals data received");
      }
      if (configuredPortalsRaw.length > 0 && Array.isArray(configuredPortalsRaw[0])) {
        configuredPortalsRaw = configuredPortalsRaw[0];
      }

      const uniquePortalsMap = new Map();
      configuredPortalsRaw.forEach((portal) => {
        if (!uniquePortalsMap.has(portal.portal_id)) {
          uniquePortalsMap.set(portal.portal_id, portal);
        }
      });

      const organizationNames = configuredPortalsRaw.map((cp) => cp.organizationName || "");
      const allSameOrganization = organizationNames.every((name) => name === organizationNames[0]);

      const mappedPortals = configuredPortalsRaw.map((cp) => {
        const staticPortal = Object.values(portals).find((p) => p.id === cp.portal_id);
        const role = localStorage.getItem("role");
        const isAuthError = ["400", "401", "403"].includes(String(cp.status));
        const isPending = String(cp.status) === "-1";
        const portalData = {
          id: cp.portal_id,
          databaseId: cp.configured_portal_id ? cp.configured_portal_id : null,
          organizationId: cp.organizationId || "",
          organizationName: cp.organizationName || "",
          organizationType: cp.organizationType || null,
          name: cp.message || "",
          display: staticPortal ? staticPortal.display : cp.name || "",
          region: cp.region || null,
          logo: staticPortal ? staticPortal.logo : null,
          status: cp.active === 1 ? "active" : "inactive",
          backgroundColor: staticPortal ? staticPortal.backgroundColor : "#575757",
          configuredId: cp.configured_portal_id,
          Message: isAuthError
            ? "Authentication Error"
            : isPending
            ? "Authenticating"
            : cp.minutes_ago < (isSleeping ? 10 : 60)
            ? "Online"
            : "Offline",
          StatusCode: cp.status || "200",
          StatusMessage: cp.status_message || "OK",
          LastFetch: cp.last_fetch ? getTimeElapsed(cp.last_fetch) : "N/A",
          LastCaseFetched: cp.last_case_fetched ? getTimeElapsed(cp.last_case_fetched) : "N/A",
          active: cp.active,
          MinutesSinceLastFetch: cp.minutes_ago || 0,
          isConfigured: true,
          WarnUser: cp.warn_user || false,
          WarnAdmin: cp.warn_admin || false,
          allSameOrganization,
        };
        return {
          ...portalData,
          hasIssues: isAuthError || isPending || portalData.WarnUser === 1,
        };
      });

      const configuredIds = new Set(mappedPortals.map((p) => p.id));
      const isCadOrg = localStorage.getItem("org_type") === "CAD";
      const notAddedPortals = Object.values(portals)
        .filter((p) => !configuredIds.has(p.id))
        .map((p) => ({
          unavailable: isCadOrg && p.id !== 1,
          id: p.id,
          databaseId: null,
          name: p.name,
          display: p.display || p.name,
          logo: p.logo,
          status: "not_added",
          backgroundColor: p.backgroundColor || "#575757",
          configuredId: null,
          Message: "",
          StatusCode: null,
          StatusMessage: "Not Configured",
          LastFetch: null,
          LastCaseFetched: null,
          active: false,
          isConfigured: false,
          hasIssues: false,
        }));

      setConfiguredPortals([...mappedPortals, ...notAddedPortals]);
      setAnimationKey(Date.now());
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to fetch configured portals");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!showContent || pollingBlocked) return;
    fetchConfiguredPortals();
    const interval = setInterval(fetchConfiguredPortals, 10000);
    return () => clearInterval(interval);
  }, [showContent, pollingBlocked]);

  const computeCardPositions = (portals) => {
    if (!portals.length || !containerRef.current) {
      return portals.map((p) => ({
        ...p,
        position: {
          isFirstInRow: true, isLastInRow: true,
          isFirstInColumn: true, isLastInColumn: true, columnIndex: 0,
        },
      }));
    }
    const containerWidth = containerRef.current.offsetWidth || 1820;
    const cardsPerRow = Math.max(Math.floor(containerWidth / cardWidth), 1);
    const totalRows = Math.ceil(portals.length / cardsPerRow);
    return portals.map((portal, index) => {
      const row = Math.floor(index / cardsPerRow);
      const col = index % cardsPerRow;
      const isLastRow = row === totalRows - 1;
      const isLastCol = col === cardsPerRow - 1 || index === portals.length - 1;
      const hasCardBelow = (index + cardsPerRow) < portals.length;
      return {
        ...portal,
        position: {
          isFirstInRow: col === 0 || portals.length === 1,
          isLastInRow: isLastCol || portals.length === 1,
          isFirstInColumn: row === 0 || portals.length === 1,
          isLastInColumn: isLastRow || !hasCardBelow || portals.length === 1,
          columnIndex: col,
        },
      };
    });
  };

  useEffect(() => {
    if (!showContent || loading || error || !containerRef.current) return;
    const handleResize = () => {
      if (!containerRef.current) return;
      const offlineThreshold = isSleeping ? 10 : 60;
      const offline = configuredPortals.filter(
        (p) => p.status === "inactive" || ["400", "401", "403", "-1"].includes(String(p.StatusCode)) || p.MinutesSinceLastFetch >= offlineThreshold
      );
      const online = configuredPortals.filter(
        (p) => p.status === "active" && !["400", "401", "403", "-1"].includes(String(p.StatusCode)) && p.MinutesSinceLastFetch < offlineThreshold
      );
      const notAdded = configuredPortals.filter((p) => p.status === "not_added" && !p.unavailable);
      const notAvailable = configuredPortals.filter((p) => p.status === "not_added" && p.unavailable);

      const offlinePositions  = computeCardPositions(offline);
      const onlinePositions   = computeCardPositions(online);
      const notAddedPositions = computeCardPositions(notAdded);
      const notAvailablePositions = computeCardPositions(notAvailable);

      const getMaxCardsPerRow = (positions) => {
        const cardsPerRow = Math.floor(containerRef.current.offsetWidth / cardWidth) || 1;
        let maxCards = 0;
        for (let i = 0; i < positions.length; i += cardsPerRow) {
          maxCards = Math.max(maxCards, Math.min(cardsPerRow, positions.length - i));
        }
        return maxCards;
      };

      const maxCards = Math.max(
        getMaxCardsPerRow(offlinePositions),
        getMaxCardsPerRow(onlinePositions),
        getMaxCardsPerRow(notAddedPositions),
        getMaxCardsPerRow(notAvailablePositions)
      );
      const contentWidth = maxCards * cardWidth;
      const calculatedMarginLeft = (containerRef.current.offsetWidth - contentWidth) / 2;

      setMaxContentWidth(contentWidth);
      setMarginLeft(Math.max(calculatedMarginLeft, 0));
      setComputedOfflinePortals(offlinePositions);
      setComputedOnlinePortals(onlinePositions);
      setComputedNotAddedPortals(notAddedPositions);
      setComputedNotAvailablePortals(notAvailablePositions);
    };

    handleResize();
    resizeListenerRef.current = handleResize;
    window.addEventListener("resize", resizeListenerRef.current);
    return () => { if (resizeListenerRef.current) window.removeEventListener("resize", resizeListenerRef.current); };
  }, [showContent, loading, error, configuredPortals, animationKey, cardWidth]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); handleCloseClick(); }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleCloseClick = () => {
    if (resizeListenerRef.current) window.removeEventListener("resize", resizeListenerRef.current);
    setIsVisible(false);
    setTimeout(() => { onBack(); }, 250);
  };

  const handleAddAnotherPortal = (portal) => {
    setSelectedPortalForConfig({
      id: portal.id, databaseId: null, name: portal.name,
      display: portal.display || portal.name, logo: portal.logo,
      status: "not_added", backgroundColor: portal.backgroundColor || "#575757",
      configuredId: null, Message: "", StatusCode: null,
      StatusMessage: "Not Configured", LastFetch: null, LastCaseFetched: null,
      active: false, isConfigured: false, hasIssues: false,
      position: { isFirstInRow: true, isLastInRow: true, isFirstInColumn: true, isLastInColumn: true, columnIndex: 0 },
    });
  };

  const handleDialogClose = () => setSelectedPortalForConfig(null);

  const handlePortalAdded = (newPortal) => {
    setConfiguredPortals((prev) => {
      const updated = prev.filter((p) => !(p.id === newPortal.id && p.status === "not_added"));
      return [...updated, newPortal];
    });
    setAnimationKey(Date.now());
  };

  const renderPortalCards = (portalList, keyPrefix) =>
    portalList.map((portal) => (
      <motion.div
        key={`${keyPrefix}-${portal.databaseId || portal.id}`}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        custom={{ columnIndex: portal.position.columnIndex }}
        layout
        onUpdate={noWaapi}
      >
        <PortalCard
          portal={portal}
          position={portal.position}
          animationKey={animationKey}
          setPollingBlocked={setPollingBlocked}
          onAddAnother={handleAddAnotherPortal}
          onDialogClose={handleDialogClose}
          onPortalAdded={handlePortalAdded}
          isSleeping={isSleeping}
        />
      </motion.div>
    ));

  return (
    <Box sx={{ width: "100%", height: "100%", position: "relative", color: "#fff", overflow: "hidden", zIndex: 1001 }}>
      {backgroundImages.srcsetW ? (
        <DashboardBackground key={backgroundImages.src} images={backgroundImages} />
      ) : (
        <img
          src={backgroundImages.src}
          // Schermvullend met object-fit: cover, dus kiezen op de getoonde
          // breedte i.p.v. alleen op pixeldichtheid (zie getBackground in App.js)
          srcSet={backgroundImages.srcsetW || backgroundImages.srcset || ""}
          sizes={backgroundImages.srcsetW ? backgroundImages.sizes : undefined}
          alt="Background"
          style={bgStyle}
        />
      )}
      <Fade in={isVisible} timeout={500}>
        <div key="fade-container" style={{ position: "relative", zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2, ease: "easeOut" }}
            onAnimationComplete={() => setShowContent(true)}
            onUpdate={noWaapi}
          >
            <motion.button
              className="close-button"
              onClick={handleCloseClick}
              variants={closeButtonVariants}
              initial="hidden"
              animate="visible"
              onUpdate={noWaapi}
              // Zelfde stijl als de sluitknop in de CaseDetailDrawer. Inline,
              // omdat .close-button in meerdere css-bestanden anders gestyled is.
              whileHover={{ backgroundColor: "rgba(255, 255, 255, 0.2)" }}
              style={{
                position: "absolute", top: 16, right: 40, zIndex: 3,
                display: "flex", width: 48, height: 48, padding: 0, boxSizing: "border-box",
                justifyContent: "center", alignItems: "center", flexShrink: 0,
                borderRadius: "50%",
                border: "1px solid rgba(255, 255, 255, 0.10)",
                backgroundColor: "rgba(255, 255, 255, 0.10)",
                backdropFilter: "blur(4px)",
                cursor: "pointer",
              }}
            >
              <CloseIcon />
            </motion.button>

            <Typography variant="h4" gutterBottom sx={{
              fontFamily: "DM Sans", fontSize: { xs: "28px", md: "40px" },
              textAlign: "left", marginLeft: "40px", mt: 2,
            }}>
              {isSleeping ? "View Portals" : "Manage Portals"}
            </Typography>

            {loading ? (
              <Box sx={{ mt: 4, marginLeft: "40px", display: "flex", alignItems: "center", gap: 2 }}>
                <CircularProgress size={20} sx={{ color: "rgba(255,255,255,0.6)" }} />
                <Typography sx={{ fontFamily: "DM Sans", fontSize: "16px", color: "rgba(255,255,255,0.6)" }}>
                  Loading portals
                </Typography>
              </Box>
            ) : error ? (
              <Typography sx={{ mt: 4, marginLeft: "40px" }}>Error: {error}</Typography>
            ) : (
              <Box sx={{
                width: "100%", maxHeight: "90vh", overflowY: "auto", mt: 4,
                marginLeft: `${marginLeft}px`,
                "&::-webkit-scrollbar": { display: "none" },
                msOverflowStyle: "none", scrollbarWidth: "none",
              }} ref={containerRef}>

                {computedOfflinePortals.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography fontSize={{ xs: "20px", md: "20px" }} sx={{ fontFamily: "DM Sans", mb: 2 }}>
                      {isSleeping ? "Sleeping" : "Offline"}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-start", gap: 0 }}>
                      {renderPortalCards(computedOfflinePortals, "offline")}
                    </Box>
                  </Box>
                )}

                {computedOnlinePortals.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography fontSize={{ xs: "20px", md: "20px" }} sx={{ fontFamily: "DM Sans", mb: 2 }}>Online</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-start", gap: 0 }}>
                      {renderPortalCards(computedOnlinePortals, "online")}
                    </Box>
                  </Box>
                )}

                {computedNotAddedPortals.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography fontSize={{ xs: "20px", md: "20px" }} sx={{ fontFamily: "DM Sans", mb: 2 }}>Not Yet Added</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-start", gap: 0 }}>
                      {renderPortalCards(computedNotAddedPortals, "not-added")}
                    </Box>
                  </Box>
                )}

                {computedNotAvailablePortals.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography fontSize={{ xs: "20px", md: "20px" }} sx={{ fontFamily: "DM Sans", mb: 2 }}>Not Available</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-start", gap: 0 }}>
                      {renderPortalCards(computedNotAvailablePortals, "not-available")}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </motion.div>
        </div>
      </Fade>

      {selectedPortalForConfig && (
        <PortalCard
          key={`add-another-${selectedPortalForConfig.id}`}
          portal={selectedPortalForConfig}
          position={{ isFirstInRow: true, isLastInRow: true, isFirstInColumn: true, isLastInColumn: true, columnIndex: 0 }}
          animationKey={animationKey}
          setPollingBlocked={setPollingBlocked}
          onAddAnother={handleAddAnotherPortal}
          onDialogClose={handleDialogClose}
          onPortalAdded={handlePortalAdded}
          autoOpenDialog={true}
          isSleeping={isSleeping}
        />
      )}
    </Box>
  );
}

export default ManagePortals;